import { api } from "../api";
import { openModal, closeModal, modalBody } from "../components/modal";
import { confirmDialog, infoDialog } from "../components/confirm-dialog";
import { canWrite, writeOnly, readOnlyNotice } from "../utils/permissions";
import { describeStage } from "../utils/stage";
import { formatDate } from "../utils/date-utils";
import type { SchoolClass, DepartmentGroup, CourseModel } from "../types";
import { esc } from "../utils/html";

let classes: SchoolClass[] = [];
let departmentGroups: DepartmentGroup[] = [];
let courseModels: CourseModel[] = [];
let currentEl: HTMLElement | null = null;
let searchQuery = "";

// Kursbeginn und Ausbildungsmodell gehoeren zum Stundenplan-Modul und erscheinen
// nur, wenn die Schule es gebucht hat. Wird von app.ts nach dem Login gesetzt.
let stundenplanEnabled = false;
export function setUserContext(features: string[]) {
  stundenplanEnabled = (features || []).includes("stundenplan");
}

async function load() {
  [classes, departmentGroups] = await Promise.all([
    api.classes.list(),
    api.settings.departmentGroups(),
  ]);
  if (stundenplanEnabled) {
    // Ohne Modelle bleibt nur die Auswahl leer, die Seite funktioniert weiter.
    try { courseModels = await api.stundenplan.courseModels.list(); }
    catch { courseModels = []; }
  }
}

// Global handlers for onclick attributes
(window as any).__cEdit = (id: number) => {
  try {
    const c = classes.find(cl => cl.id == id);
    if (c) openForm(c);
  } catch (err: any) { alert("Fehler: " + err.message); }
};
(window as any).__cDel = async (id: number) => {
  const cls = classes.find(c => c.id == id);
  const ok = await confirmDialog({
    title: "Klasse löschen",
    message: cls ? `„${cls.name}" wird gelöscht. Schüler dieser Klasse bleiben erhalten und verlieren ihre Klassenzuordnung.`
                 : "Klasse wirklich löschen?",
    confirmLabel: "Löschen", danger: true,
  });
  if (!ok) return;
  try {
    await api.classes.delete(id);
    await load();
    if (currentEl) renderTable(currentEl);
  } catch (err: any) {
    // 409: Für die Klasse liegt ein Stundenplan vor, der mitgelöscht würde.
    if (err.status === 409) await infoDialog("Klasse wird noch verwendet", err.message);
    else alert("Fehler: " + err.message);
  }
};

function renderTable(el: HTMLElement) {
  const tbody = el.querySelector("tbody")!;
  const q = searchQuery.trim().toLowerCase();
  const filtered = q
    ? classes.filter(c => c.name.toLowerCase().includes(q))
    : classes;
  const cols = 3 + (stundenplanEnabled ? 1 : 0) + (canWrite() ? 1 : 0);
  if (filtered.length === 0) {
    tbody.innerHTML = `<tr><td colspan="${cols}" class="empty-state">${q ? "Keine Treffer" : "Keine Klassen vorhanden"}</td></tr>`;
    return;
  }
  tbody.innerHTML = filtered.map(c => {
    const groupName = c.customGroupName ?? c.departmentGroupName ?? "–";
    // Ohne Kursbeginn oder Modell gibt es keine Stufe; dann bleibt die Zelle leer.
    const stage = c.stageLabel
      ? `${esc(c.stageLabel)}<div style="font-size:.75rem;opacity:.7">seit ${esc(formatDate(c.courseStartDate))}</div>`
      : "";
    return `
    <tr style="cursor:pointer" onclick="window.__cEdit(${c.id})">
      <td style="font-weight:500">${esc(c.name)}</td>
      <td class="text-muted">${esc(groupName)}</td>
      ${stundenplanEnabled ? `<td class="text-muted">${stage}</td>` : ""}
      <td class="text-muted">${c.studentCount}</td>
      ${writeOnly(`<td style="white-space:nowrap"><button class="btn btn-danger" onclick="event.stopPropagation();window.__cDel(${c.id})">Löschen</button></td>`)}
    </tr>`;
  }).join("");
}

function openForm(cls?: SchoolClass) {
  const isEdit = !!cls;

  // Aktuell ausgewählter Wert im Format "p-{id}" oder "c-{id}"
  let currentVal = "";
  if (cls?.customGroupId) currentVal = `c-${cls.customGroupId}`;
  else if (cls?.departmentGroupId) currentVal = `p-${cls.departmentGroupId}`;

  const groupOpts = `<option value="">– Keine / Standard –</option>` +
    departmentGroups
      .filter(g => g.isActive !== false || currentVal === `${g.isCustom ? "c" : "p"}-${g.id}`)
      .map(g => {
        const val = `${g.isCustom ? "c" : "p"}-${g.id}`;
        const selected = currentVal === val ? "selected" : "";
        return `<option value="${val}" ${selected}>${esc(g.name)}</option>`;
      }).join("");

  // Betrachter sehen dieselbe Maske schreibgeschützt, damit die Details lesbar bleiben.
  const readOnly = !canWrite();
  const disabled = readOnly ? " disabled" : "";

  const stundenplanFields = !stundenplanEnabled ? "" : `
    <div class="form-group">
      <label>Kursbeginn</label>
      <input type="date" id="f-start" value="${esc(cls?.courseStartDate || "")}"${disabled} />
    </div>
    <div class="form-group">
      <label>Ausbildungsmodell</label>
      <select id="f-model"${disabled}></select>
      ${courseModels.length === 0
        ? `<small class="text-muted">Noch keine Modelle angelegt. Einstellungen, Stundenplan, Ausbildungsmodelle.</small>`
        : `<small class="text-muted">Zur Auswahl stehen die Modelle der gewählten Fachbereichsgruppe.</small>`}
    </div>
    <div class="alert alert-info" id="f-stage-hint" style="font-size:.8125rem"></div>`;

  // Namensliste der Klasse. Die Übersicht zeigt nur eine Zählung, und "wer ist eigentlich
  // in dieser Klasse" ließ sich bisher nur über den gefilterten Gesamtbestand beantworten.
  // Nur beim Bearbeiten: eine neue Klasse hat noch niemanden.
  const rosterBlock = !isEdit ? "" : `
    <div class="form-group">
      <label>Schüler</label>
      <div id="f-roster" style="max-height:12rem;overflow-y:auto;border:1px solid var(--gray-300);border-radius:.375rem;padding:.5rem .75rem">
        <span class="text-muted">Wird geladen…</span>
      </div>
    </div>`;

  openModal(isEdit ? (readOnly ? "Klasse" : "Klasse bearbeiten") : "Neue Klasse", `
    <div class="form-group"><label>Name</label><input id="f-name" value="${esc(cls?.name || "")}"${disabled} /></div>
    <div class="form-group"><label>Fachbereichsgruppe</label><select id="f-group"${disabled}>${groupOpts}</select></div>
    ${stundenplanFields}
    ${rosterBlock}
    <div class="form-actions">
      <button class="btn btn-secondary" id="f-cancel">${readOnly ? "Schließen" : "Abbrechen"}</button>
      ${writeOnly(`<button class="btn btn-primary" id="f-save">Speichern</button>`)}
    </div>`, { wide: false });

  const mb = modalBody();

  // Nachladen statt blockieren: Die Maske steht sofort, die Liste kommt nach.
  const roster = mb.querySelector("#f-roster") as HTMLElement | null;
  if (roster && cls) {
    api.classes.students(cls.id).then(students => {
      roster.innerHTML = students.length === 0
        ? `<span class="text-muted">Dieser Klasse ist noch niemand zugeordnet.</span>`
        : students.map(s => `<div>${esc(`${s.lastName}, ${s.firstName}`)}</div>`).join("");
    }).catch(err => {
      roster.innerHTML = `<span class="text-muted">Konnte nicht geladen werden: ${esc(err.message)}</span>`;
    });
  }

  const groupSelect = mb.querySelector("#f-group") as HTMLSelectElement;
  const startInput = mb.querySelector("#f-start") as HTMLInputElement | null;
  const modelSelect = mb.querySelector("#f-model") as HTMLSelectElement | null;
  const hint = mb.querySelector("#f-stage-hint") as HTMLElement | null;

  // Ein Modell nennt seinen Ausbildungsgang. Zur Auswahl stehen deshalb nur die Modelle
  // der gewaehlten Fachbereichsgruppe plus die, die fuer alle Gaenge gelten. Das Backend
  // weist eine unpassende Zuordnung ohnehin zurueck.
  const rebuildModels = () => {
    if (!modelSelect) return;
    const groupKey = groupSelect.value;
    const selected = modelSelect.value || (cls?.courseModelId ? String(cls.courseModelId) : "");
    const fitting = courseModels.filter(m =>
      (m.active || m.id === cls?.courseModelId) && (!m.groupKey || m.groupKey === groupKey));
    modelSelect.innerHTML = `<option value="">Kein Modell</option>` +
      fitting.map(m => `<option value="${m.id}" ${selected === String(m.id) ? "selected" : ""}>${esc(m.name)}</option>`).join("");
  };
  rebuildModels();

  // Zeigt sofort, was aus Kursbeginn und Modell folgt, damit die Bezeichnung
  // nicht erst nach dem Speichern sichtbar wird.
  const updateHint = () => {
    if (!hint || !startInput || !modelSelect) return;
    const model = courseModels.find(m => m.id === +modelSelect.value);
    const label = describeStage(startInput.value || null, model);
    if (!startInput.value) {
      hint.textContent = "Ohne Kursbeginn wird kein Jahrgang berechnet.";
    } else if (!model) {
      hint.textContent = "Ohne Ausbildungsmodell wird kein Jahrgang berechnet.";
    } else if (label === null) {
      // Kursbeginn liegt in der Zukunft: es gibt noch keine laufende Stufe.
      hint.textContent = `Der Kurs beginnt erst am ${formatDate(startInput.value)}.`;
    } else if (label === "Abgeschlossen") {
      hint.textContent = `Dieser Kurs ist nach dem Modell „${model.name}" bereits abgeschlossen.`;
    } else {
      // Neutrale Formulierung, weil eine Stufe auch "Mittelkurs" heissen kann.
      hint.textContent = `Aktueller Jahrgang: ${label}`;
    }
  };
  updateHint();

  mb.querySelector("#f-cancel")!.addEventListener("click", closeModal);
  if (readOnly) return;

  startInput?.addEventListener("input", updateHint);
  modelSelect?.addEventListener("change", updateHint);
  groupSelect.addEventListener("change", () => { rebuildModels(); updateHint(); });

  mb.querySelector("#f-save")!.addEventListener("click", async () => {
    const name = (mb.querySelector("#f-name") as HTMLInputElement).value.trim();
    if (!name) return;
    const raw = groupSelect.value;
    let departmentGroupId: number | null = null;
    let customGroupId: number | null = null;
    if (raw.startsWith("p-")) departmentGroupId = +raw.slice(2) || null;
    else if (raw.startsWith("c-")) customGroupId = +raw.slice(2) || null;

    // Ohne Modul die Felder gar nicht mitschicken, damit das Backend sie nicht anfasst.
    const data: any = { name, departmentGroupId, customGroupId };
    if (stundenplanEnabled) {
      data.courseStartDate = startInput?.value || null;
      data.courseModelId = modelSelect?.value ? +modelSelect.value : null;
    }
    if (isEdit) await api.classes.update(cls!.id, data);
    else await api.classes.create(data);
    closeModal(); await load(); if (currentEl) renderTable(currentEl);
  });
  (mb.querySelector("#f-name") as HTMLInputElement).focus();
}

export async function render(el: HTMLElement) {
  currentEl = el;
  searchQuery = "";
  el.innerHTML = `
    <div class="page-header"><h1>Klassen</h1>${writeOnly(`<button class="btn btn-primary" id="btn-add">+ Neue Klasse</button>`)}</div>
    ${readOnlyNotice()}
    <div class="filter-bar"><input type="search" id="search-query" placeholder="Suchen …" /></div>
    <div class="card table-wrap"><table><thead><tr><th>Name</th><th>Fachbereichsgruppe</th>${stundenplanEnabled ? `<th style="width:200px">Jahrgang</th>` : ""}<th>Schüler</th>${writeOnly(`<th style="width:200px">Aktionen</th>`)}</tr></thead><tbody></tbody></table></div>`;
  el.querySelector("#btn-add")?.addEventListener("click", () => openForm());
  await load();
  const searchInput = el.querySelector("#search-query") as HTMLInputElement;
  searchInput.addEventListener("input", () => { searchQuery = searchInput.value; renderTable(el); });
  renderTable(el);
}
